import React, { useState } from 'react'
import { useCustomFetch } from '../../hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

interface OnboardingAddMemberProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function OnboardingAddMember({ open, onOpenChange, onSuccess }: OnboardingAddMemberProps) {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const customFetch = useCustomFetch()

  async function inviteMembers() {
    if (email.length === 0) return toast.error('Please enter an email address')
    // verify valid emails
    const emails = email
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0)
    for (const e of emails) {
      const emailPattern = /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/
      if (!emailPattern.test(e)) return toast.error('Invalid email address: ' + e)
    }

    setIsLoading(true)
    try {
      setEmail('')
      const response = await customFetch('/inviteMembers', 'POST', { emails })

      // Always mark as success if API call succeeded (even if users array is empty - means already invited)
      if (response.users !== undefined) {
        if (response.users.length > 0) {
          toast.success(`Successfully invited ${response.users.length} member(s)!`)
        } else {
          toast.success('Invitation processed! (Some emails may have already been invited)')
        }
        onSuccess() // Mark team step as completed and close dialog
      } else {
        toast.error('Unexpected response from server. Please try again.')
      }
    } catch (error) {
      toast.error('Failed to invite members. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-2xl p-0 flex flex-col max-h-[90vh]">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-xl font-semibold">Add Members to Workspace</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="w-full flex items-center justify-between gap-4">
            <input
              className="grow pl-4 pr-8 py-2.5 shadow-inner border border-grey-200 bg-grey-100 rounded text-zinc text-base font-medium font-['Manrope'] disabled:opacity-50"
              placeholder="Emails, separated by commas"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isLoading && inviteMembers()}
              disabled={isLoading}
            />
            <Button
              className="bg-[#FF4A8E] hover:bg-[#FF4A8E]/90 text-white"
              onClick={inviteMembers}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                'Invite'
              )}
            </Button>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
